import { Wordmark } from '@/Components/Wordmark';

/**
 * Page footer. Wordmark, copyright, link columns. Compact "mobile" variant
 * collapses to a single column.
 *
 *   <Footer mobile={false} />
 */

export interface FooterLink {
    label: string;
    href: string;
}

export interface FooterColumn {
    heading: string;
    links: FooterLink[];
}

export interface FooterProps {
    mobile?: boolean;
    columns?: FooterColumn[];
    className?: string;
}

const DEFAULT_COLUMNS: FooterColumn[] = [
    {
        heading: 'Directory',
        links: [
            { label: 'Practice areas', href: '/' },
            { label: 'States', href: '/' },
            { label: 'Cities', href: '/' },
            { label: 'Search', href: '/search' },
        ],
    },
    {
        heading: 'For people',
        links: [
            { label: 'Get matched', href: '/get-matched' },
            { label: 'Resources', href: '/resources' },
            { label: 'How it works', href: '/' },
        ],
    },
    {
        heading: 'For attorneys',
        links: [
            { label: 'Claim your profile', href: '/for-attorneys' },
            { label: 'Sponsored listings', href: '/for-attorneys' },
            { label: 'Sign in', href: '/auth/login' },
        ],
    },
    {
        heading: 'About',
        links: [
            { label: 'Editorial standards', href: '/' },
            { label: 'Privacy', href: '/' },
            { label: 'Terms', href: '/' },
        ],
    },
];

export function Footer({
    mobile = false,
    columns = DEFAULT_COLUMNS,
    className,
}: FooterProps) {
    const cls = [
        'bg-paper-deep border-t border-rule text-ink-soft',
        'px-6 py-10',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <footer className={cls}>
            <div className="max-w-7xl mx-auto">
                <div
                    className={[
                        'grid gap-8',
                        mobile
                            ? 'grid-cols-1'
                            : 'grid-cols-1 md:grid-cols-5 md:gap-12',
                    ].join(' ')}
                >
                    <div className={mobile ? '' : 'md:col-span-1'}>
                        <Wordmark size={20} sub="Personal injury directory" />
                    </div>
                    {columns.map((col) => (
                        <nav key={col.heading} aria-label={col.heading}>
                            <h2 className="font-sans text-[12px] uppercase tracking-widest text-ink-mute mb-3">
                                {col.heading}
                            </h2>
                            <ul className="flex flex-col gap-2">
                                {col.links.map((link) => (
                                    <li key={link.label}>
                                        <a
                                            href={link.href}
                                            className="text-[14px] hover:text-ink"
                                        >
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    ))}
                </div>
                <div className="mt-10 pt-6 border-t border-rule text-[12px] text-ink-mute flex flex-col md:flex-row gap-2 md:gap-6">
                    <span>© injury.help · Editorial directory, not a law firm.</span>
                    <span>
                        Information here is educational; consult a licensed
                        attorney for legal advice.
                    </span>
                </div>
            </div>
        </footer>
    );
}

export default Footer;
